// shared/schemas/common.schemas.ts
// Common Zod schemas used across frontend, mobile, and backend
import { z } from 'zod';

// ============================================================
// ObjectId Schemas
// Accepts ObjectId instances or strings, coerces to string
// ============================================================

/**
 * MongoDB ObjectId schema.
 * Uses z.coerce.string() which calls String(val) - works with ObjectId.toString()
 */
export const objectIdSchema = z.coerce.string().describe('MongoDB ObjectId');

/**
 * Nullable ObjectId - preserves null instead of coercing to "null" string
 */
export const nullableObjectIdSchema = z
	.preprocess((val) => (val === null || val === undefined ? null : val), z.coerce.string().nullable())
	.describe('MongoDB ObjectId or null');

/**
 * Optional ObjectId - preserves undefined
 */
export const optionalObjectIdSchema = z
	.preprocess((val) => (val === undefined ? undefined : val), z.coerce.string().optional())
	.describe('MongoDB ObjectId (optional)');

// ============================================================
// Date Schemas
// ============================================================

/**
 * Date schema for response serialization.
 * Accepts Date instances or ISO strings, transforms to ISO string.
 * Similar to objectIdSchema pattern for consistent serialization.
 */
export const dateSchema = z
	.union([z.string(), z.date()])
	.transform((val) => (val instanceof Date ? val.toISOString() : val))
	.pipe(z.string())
	.describe('ISO 8601 date string');

/**
 * Nullable date schema for optional date fields.
 */
export const nullableDateSchema = z
	.union([z.string(), z.date(), z.null()])
	.transform((val) => {
		if (val === null) return null;
		return val instanceof Date ? val.toISOString() : val;
	})
	.pipe(z.string().nullable())
	.describe('ISO 8601 date string or null');

// ============================================================
// Date Field Schemas (union: accepts Date or ISO string, outputs Date)
// Use in entity schemas. Handles both MongoDB Date objects and HTTP string input.
// ============================================================

/**
 * Accepts Date objects or ISO 8601 datetime strings, always outputs Date.
 * - MongoDB/service layer: Date passes z.date() branch, transform is no-op
 * - HTTP/JSON input: string passes z.iso.datetime() branch, transform converts to Date
 * Rejects non-ISO strings, numbers, booleans (strict unlike z.coerce.date())
 */
export const dateField = z
	.union([z.iso.datetime(), z.date()])
	.transform((val) => (typeof val === 'string' ? new Date(val) : val));

/**
 * Same as dateField but accepts null.
 */
export const nullableDateField = z
	.union([z.iso.datetime(), z.date()])
	.nullable()
	.transform((val) => (typeof val === 'string' ? new Date(val) : val));

// ============================================================
// Phone Number Schemas
// ============================================================

/**
 * Phone number field — always present, either a string or null.
 */
export const phoneNumberField = z.string().nullable().default(null);
export type PhoneNumber = z.infer<typeof phoneNumberField>;

// ============================================================
// Generic Record Schemas
// ============================================================

/**
 * Generic string-keyed record with any values.
 * Use for denormalized/embedded documents where the shape varies at runtime.
 */
export const anyRecordSchema = z.record(z.string(), z.any()).describe('Generic string-keyed record');

// ============================================================
// String Schemas
// ============================================================

/**
 * Required string that rejects empty strings
 */
export const requiredStringSchema = z.string().min(1, { message: 'String cannot be empty' });

/**
 * Email schema with validation (Zod 4 syntax)
 */
export const emailSchema = z.email().describe('Valid email address');

// ============================================================
// Type Exports
// ============================================================

export type ObjectIdString = z.infer<typeof objectIdSchema>;
export type NullableObjectIdString = z.infer<typeof nullableObjectIdSchema>;
export type OptionalObjectIdString = z.infer<typeof optionalObjectIdSchema>;
